/**
 * ProShip Direct v2 — edit a connected courier account.
 *
 * A drawer rather than a modal: credentials and contract attributes together run to twenty-odd
 * fields, and a dialog that tall stops being a dialog.
 *
 * The account code is the one thing that cannot change. It is what every shipment already
 * dispatched on this account carries, what its rate card and serviceability are keyed on, and what
 * a rule naming this courier points at. Renaming it would silently orphan all three, so it is
 * shown locked with the reason rather than left out — a merchant who wants a different code should
 * connect a second account, which is supported.
 *
 * SAVING IS THE TEST. There is no separate "re-test" button any more: the server probes the new
 * credentials against the carrier and only writes them to PCIS once they pass, so a rejected edit
 * leaves a working account working. A button that tested without saving would have let a merchant
 * watch a green tick and then save something else.
 *
 * THE CREDENTIAL FIELDS COME FROM THE SERVER, passed in by the caller, for the same reason they do
 * on the connect screen: the key names must match what the PCIS adapter deserialises. A local
 * fixture here produced an account that saved cleanly and failed at the first AWB.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Drawer, Grid, Typography, Button, IconButton, Divider, Alert, CircularProgress, Tooltip,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';

import { FormProvider, RHFTextField } from '../../../common/hook-form';
import { primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, LIGHT_BG, WHITE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP } from '../../../styles/foundation/spacing';

import { CREDENTIAL_STATE, COURIER_META_FIELDS, defaultCourierMeta } from '../data/defaults';
import { CourierMetaFields, WebhookPanel } from './courierFields';
import { CredentialChip } from './chips';
import { MONO } from './directCellStyles';

const labelSx = {
  fontFamily: FONT,
  fontSize: SIZE['3xs'],
  fontWeight: WEIGHT.bold,
  color: SUBTLE,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};
const bodySx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.55 };
const sectionSx = { p: 2, borderBottom: `1px solid ${BORDER}` };

/**
 * What the merchant entered, for every field this carrier asks for.
 *
 * Credentials come from the server with every SECRET masked (bullets and the last four
 * characters); the secret itself never reaches the browser. Sent back unchanged, a mask tells the
 * server to keep the saved value, so changing one field does not mean retyping the others.
 */
const savedValues = (credFields, detail) => {
  const saved = new Map((detail?.credentials || []).map((c) => [c.fieldKey, c.value ?? '']));
  return {
    cred: credFields.reduce((acc, f) => ({ ...acc, [f.fieldKey]: saved.get(f.fieldKey) ?? '' }), {}),
    meta: COURIER_META_FIELDS.reduce(
      (acc, f) => ({ ...acc, [f.key]: detail?.meta?.[f.key] ?? defaultCourierMeta()[f.key] }),
      {}
    ),
  };
};

/* The prefix every masked secret starts with — see maskSecret on the server. */
const MASK = '\u2022'.repeat(8);

export default function CourierEditDrawer({ account, open, credFields = [], detail, schemaError, onClose, onSave }) {
  const [saving, setSaving] = useState(false);
  /* { success, message } from the server — the carrier's own verdict on this edit. */
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);

  const defaults = useMemo(() => savedValues(credFields, detail), [credFields, detail]);
  const methods = useForm({ defaultValues: defaults });
  const { handleSubmit, reset, watch } = methods;
  const cred = watch('cred') || {};

  // The drawer stays mounted between rows, so the form is reloaded when the account changes.
  useEffect(() => {
    reset(defaults);
    setResult(null);
    setFailure(null);
    setSaving(false);
  }, [defaults, reset]);

  if (!account) return null;

  const hasError = account.credentials === CREDENTIAL_STATE.FAILED;
  const missing = credFields.filter((f) => f.required).length > 0;

  /**
   * Test against the carrier and, only if it passes, write.
   *
   * Two outcomes and they are not the same. The carrier rejecting the credentials is a RESULT —
   * rendered here, account untouched, drawer stays open so the merchant can correct them. A
   * request that could not be made at all throws, and that message goes to `failure`.
   */
  const submit = async (values) => {
    setSaving(true);
    setResult(null);
    setFailure(null);
    try {
      const res = await onSave(account.accountCode, { credentials: values.cred, meta: values.meta });
      setResult(res);
      if (res?.success) onClose();
    } catch (e) {
      setFailure(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 720 }, bgcolor: WHITE } }}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(submit)}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* ── Header: what cannot change ── */}
          <Box sx={{ ...sectionSx, display: 'flex', alignItems: 'flex-start', gap: GAP.md }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY }}>
                {account.carrierName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                <Typography sx={{ fontFamily: MONO, fontSize: SIZE['3xs'], color: SUBTLE }}>
                  {account.accountCode}
                </Typography>
                <Tooltip title='The account code is fixed. Every shipment already sent on this account carries it, and your rules and rate card point at it. Need a different code? Connect it as a second account.'>
                  <LockOutlinedIcon sx={{ fontSize: 13, color: SUBTLE }} />
                </Tooltip>
                <CredentialChip state={account.credentials} />
              </Box>
            </Box>
            <IconButton size='small' onClick={onClose} aria-label='close'>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {hasError && !result && (
              <Alert severity='error' sx={{ m: 2, mb: 0, fontFamily: FONT, fontSize: SIZE['3xs'] }}>
                {account.lastError}
              </Alert>
            )}

            <Box sx={sectionSx}>
              <Typography sx={{ ...labelSx, mb: 1 }}>Account</Typography>
              {/*
                Service is shown, not edited. Direction decides which carrier contract (cp_id)
                this account hangs off and which credential fields apply, so changing it is a
                different account, not an edit — the old select wrote to a field the server did
                not read.
              */}
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={5}>
                  <Typography sx={{ ...labelSx, mb: 0.3 }}>Service</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY }}>
                    {account.serviceType || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Typography sx={bodySx}>
                    Carrier, account code and service are fixed for the life of the account.
                    Credentials and the courier limits below can be changed at any time —
                    allocation picks the new values up from the next order.
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={{ ...labelSx, mb: 1 }}>Credentials · {account.carrierName}</Typography>
              {schemaError ? (
                <Alert severity='warning' sx={{ fontFamily: FONT, fontSize: SIZE['3xs'] }}>
                  {schemaError}
                </Alert>
              ) : credFields.length === 0 || !detail ? (
                <Typography sx={bodySx}>Loading {account.carrierName} fields…</Typography>
              ) : (
                <>
                  {detail.credentialsError && (
                    <Alert severity='warning' sx={{ mb: 1.5, fontFamily: FONT, fontSize: SIZE['3xs'] }}>
                      {detail.credentialsError}
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    {credFields.map((f) => {
                      /*
                        A saved secret shows as its mask in plain text, so the last four
                        characters can be recognised; once the merchant types a new one it is
                        hidden like any password.
                      */
                      const keepsSaved = String(cred[f.fieldKey] || '').startsWith(MASK);
                      return (
                        <Grid item xs={12} md={6} key={f.fieldKey}>
                          <RHFTextField
                            name={`cred.${f.fieldKey}`}
                            label={f.label}
                            mandatory={f.required}
                            type={f.fieldType === 'SECRET' && !keepsSaved ? 'password' : 'text'}
                            helperText={keepsSaved ? 'Saved. Leave as is to keep it, or clear and type a new one.' : (f.helpText || '')}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
              <Typography sx={{ ...bodySx, mt: 1.25 }}>
                Change only what has changed. Secrets are shown masked and stay as saved unless you
                replace them; every change is tested with the carrier before it is saved.
              </Typography>

              {/*
                The carrier's verdict on this edit. Only a FAILURE lands here: a pass closes the
                drawer, because the list behind it already shows the cleared state.
              */}
              {result && !result.success && (
                <Alert severity='error' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['3xs'] }}>
                  {account.carrierName} rejected these credentials, so nothing was changed —
                  the account still has the ones it had. {result.message}
                  {/* The host we called — see the note on the same block in ConnectCourier. */}
                  {result.endpoint && (
                    <Box component='div' sx={{ mt: 0.5, fontFamily: MONO, fontSize: SIZE['4xs'] }}>
                      Called: {result.endpoint}
                    </Box>
                  )}
                </Alert>
              )}
              {failure && (
                <Alert severity='warning' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['3xs'] }}>
                  {failure}
                </Alert>
              )}
            </Box>

            <Box sx={sectionSx}>
              <Typography sx={{ ...labelSx, mb: 1 }}>Courier limits</Typography>
              <CourierMetaFields />
            </Box>

            <Box sx={{ p: 2 }}>
              <Typography sx={{ ...labelSx, mb: 1 }}>Webhook URL</Typography>
              <WebhookPanel carrierName={account.carrierName} />
            </Box>
          </Box>

          <Divider sx={{ borderColor: BORDER }} />
          <Box sx={{ p: 2, display: 'flex', gap: GAP.md, bgcolor: LIGHT_BG }}>
            {/*
              One action, because saving IS the test — the server will not write credentials the
              carrier has not accepted.
            */}
            <Button
              type='submit'
              variant='contained'
              size='small'
              disabled={saving || !missing}
              startIcon={saving ? <CircularProgress size={13} /> : <ScienceOutlinedIcon />}
              sx={primaryBtnSx}
            >
              {saving ? 'Testing with the carrier…' : 'Test and save'}
            </Button>
            <Button size='small' onClick={onClose} sx={secondaryBtnSx}>
              Cancel
            </Button>
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
}
